/**
 * Outer-ring health gate — T-311 (030c Phase 2).
 *
 * Probes `/api/v1/ready` before mounting the launcher pill. Mirrors
 * prod's loader gate (`experience-ecommerce-widget/src/loader/index.ts`)
 * but consolidated into a small reusable utility.
 *
 * Schedule (matches prod):
 *   - 1 initial probe with 3s timeout
 *   - On fail: burst-retry every 1s + 100-300ms jitter for 15s
 *   - If still down: slow-poll every 5min while tab is visible
 *
 * The launcher pill has TWO mount states tied to the gate result:
 *   - "available"     — pill renders normally, click opens widget
 *   - "unavailable"   — pill renders greyed-out, click shows tooltip
 *                       "We're catching our breath, try in a moment"
 *
 * The slow-poll callback can flip the state from unavailable → available
 * mid-session if the engine recovers. Without this, a shopper who lands
 * during a deploy never sees the launcher even after the engine
 * recovers seconds later.
 *
 * NOTE on inner-ring reconciliation: the React HealthGate.tsx component
 * runs its own probe inside the iframe on widget mount. Both gates
 * probe the same endpoint with the same timeout. The duplication is
 * intentional (different failure UIs: greyed launcher vs full-screen
 * offline). Cross-tab probedAt skip optimization is post-pilot.
 */

const PROBE_TIMEOUT_MS = 3000;
const BURST_RETRY_WINDOW_MS = 15000;
const BURST_RETRY_INTERVAL_MS = 1000;
const SLOW_POLL_INTERVAL_MS = 5 * 60 * 1000;

export type HealthState = "available" | "unavailable";

async function probeReady(apiBase: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    PROBE_TIMEOUT_MS,
  );
  try {
    const resp = await fetch(`${apiBase}/api/v1/ready`, {
      method: "GET",
      signal: controller.signal,
      mode: "cors",
      credentials: "omit",
    });
    return resp.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Run the gate. Calls `onState` immediately with the initial state
 * after the first probe (or after burst-retry exhaustion), then again
 * if slow-poll recovers from unavailable → available.
 *
 * Returns a cancel function that clears the slow-poll interval.
 * The caller owns the launcher pill's lifecycle; this just signals
 * which mount state to use.
 */
export function runHealthGate(
  apiBase: string,
  onState: (state: HealthState, tenantId: string) => void,
  tenantId: string,
): () => void {
  let cancelled = false;
  let slowPollHandle: number | null = null;

  async function runInitialProbe() {
    if (await probeReady(apiBase)) {
      if (!cancelled) onState("available", tenantId);
      return;
    }
    sendBlockedBeacon(apiBase, tenantId);

    // Burst-retry window — keep trying for 15s in case engine is in
    // a deploy drain that just needs a few seconds to clear.
    const deadline = Date.now() + BURST_RETRY_WINDOW_MS;
    while (Date.now() < deadline && !cancelled) {
      const jitter = 100 + Math.random() * 200;
      await sleep(BURST_RETRY_INTERVAL_MS + jitter);
      if (cancelled) return;
      if (await probeReady(apiBase)) {
        if (!cancelled) onState("available", tenantId);
        return;
      }
    }

    // Still unavailable after burst — render greyed launcher and slow-poll.
    if (!cancelled) onState("unavailable", tenantId);
    slowPollHandle = window.setInterval(() => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      probeReady(apiBase).then((ok) => {
        if (ok && !cancelled) {
          if (slowPollHandle !== null) {
            window.clearInterval(slowPollHandle);
            slowPollHandle = null;
          }
          onState("available", tenantId);
        }
      });
    }, SLOW_POLL_INTERVAL_MS);
  }

  void runInitialProbe();

  return () => {
    cancelled = true;
    if (slowPollHandle !== null) {
      window.clearInterval(slowPollHandle);
      slowPollHandle = null;
    }
  };
}

let blockedBeaconSent = false;

function sendBlockedBeacon(apiBase: string, tenantId: string): void {
  if (blockedBeaconSent) return;
  blockedBeaconSent = true;
  try {
    const body = JSON.stringify({
      event: "loader_health_gate_blocked",
      data: { tenant_id: tenantId, timestamp: Date.now() },
    });
    const url = `${apiBase}/api/v1/telemetry`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      // Best-effort — backend is down by definition when this fires.
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* swallow */
  }
}
